import Button from "components/Button/Button";
import Card from "components/Card/Card";
import type { ClaimantType, FileType } from "types/DataTypes";

import KaboodleModal from "./KaboodleModal";

export type ClaimantMatchParams = {
  ssn: string;
  first_name: string;
  last_name: string;
  birth_date: string;
};

interface ExistingClaimantFileModalProps {
  isOpen: boolean;
  matchParams: ClaimantMatchParams;
  // The parent only renders this modal once a match exists, so this is
  // always the matched file (never null).
  matchedFile: FileType;
  matchCount: number;
  onUseExisting: () => void;
  onDismiss: () => void;
}

const digitsOnly = (value: string | null | undefined): string =>
  (value ?? "").replace(/\D/g, "");

/**
 * The claimant on the matched file that triggered the duplicate warning.
 * Undefined when none of the file's claimants matches the entered fields
 * (the claimant details are then omitted rather than showing a possibly
 * unrelated claimant).
 */
const findMatchedClaimant = (
  file: FileType,
  params: ClaimantMatchParams,
): ClaimantType | undefined =>
  (file.claimants ?? []).find(
    (claimant) =>
      (params.ssn && digitsOnly(claimant.ssn) === params.ssn) ||
      (params.first_name &&
        params.last_name &&
        params.birth_date &&
        claimant.first_name?.toLowerCase() ===
          params.first_name.toLowerCase() &&
        claimant.last_name?.toLowerCase() === params.last_name.toLowerCase()),
  );

/**
 * Shown while creating a new file when the claimant being entered (by SSN,
 * or by name + date of birth) already exists on another file. Lets the user
 * jump to the existing file or continue creating a brand-new file.
 */
const ExistingClaimantFileModal = ({
  isOpen,
  matchParams,
  matchedFile,
  matchCount,
  onUseExisting,
  onDismiss,
}: ExistingClaimantFileModalProps) => {
  const client = matchedFile.client?.name;
  const matchedClaimant = findMatchedClaimant(matchedFile, matchParams);
  const claimantName = matchedClaimant
    ? [matchedClaimant.first_name, matchedClaimant.last_name]
        .filter(Boolean)
        .join(" ")
    : null;
  const matchedBySsn = Boolean(
    matchParams.ssn &&
    matchedClaimant &&
    digitsOnly(matchedClaimant.ssn) === matchParams.ssn,
  );
  // When the matched claimant can't be pinned down, describe whichever
  // criteria could have matched (SSN wins only when it's all that was
  // entered).
  const hasNameDobParams = Boolean(
    matchParams.first_name && matchParams.last_name && matchParams.birth_date,
  );
  const matchDescription =
    matchedBySsn || !hasNameDobParams
      ? "this Social Security Number"
      : "this name and date of birth";

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onDismiss}
      footer={
        <div className="flex jc-c">
          <Button onClick={onUseExisting}>Use Existing File</Button>
          <Button onClick={onDismiss}>New File</Button>
        </div>
      }
    >
      <Card heading="Claimant Already Exists">
        <p>
          A claimant with {matchDescription} already exists on{" "}
          {matchCount > 1 ? (
            <>
              <strong>{matchCount}</strong> existing files
            </>
          ) : (
            "an existing file"
          )}
          .
        </p>
        <ul className="mrg-top">
          <li>File #{matchedFile.id}</li>
          {client && <li>Client: {client}</li>}
          {claimantName && <li>Claimant: {claimantName}</li>}
          {matchedClaimant?.birth_date && (
            <li>Date of Birth: {String(matchedClaimant.birth_date)}</li>
          )}
          {matchedFile.status_verbose && (
            <li>Status: {matchedFile.status_verbose}</li>
          )}
        </ul>
        <p className="mrg-top">
          Open the existing file, or continue creating a new file.
        </p>
      </Card>
    </KaboodleModal>
  );
};

export default ExistingClaimantFileModal;
